import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  BarChart3,
  CalendarDays,
  Cpu,
  Globe2,
  KeyRound,
  Map,
  Package,
  X,
} from "lucide-react";

import type { DatasetDetail, DatasetDashboardType } from "../types";
import { ShipmentsTable } from "../components/ShipmentsTable";
import { MapPanel } from "../components/MapPanel";
import { MapReversePanel } from "../components/MapReversePanel";
import { KeyFiguresPanel } from "../components/KeyFiguresPanel";
import { AppSelect } from "../components/AppSelect";
import { DatasetBreadcrumbNav } from "../components/DatasetBreadcrumbNav";
import { DatePicker } from "../components/DatePicker";
import { AnalysisPanel } from "../components/analysis/AnalysisPanel";
import type { AnalysisFilters } from "../components/analysis/types";
import { useAppQueryClient } from "../lib/queryClient";
import { SAFE_TEMPERATURE_RANGE } from "../lib/temperature";
import { useClientDatasets } from "../hooks/useClients";
import {
  PerfHttpError,
  fetchJsonWithPerf,
  markFrontMilestone,
  useObserveFrontQuery,
  usePagePerfTrace,
} from "../lib/perfAudit";
import {
  buildFiguresForShipments,
  filterMapDestinations,
  filterMapTransits,
  filterShipmentsByDateRange,
  getDatasetDateBounds,
  getShipmentCountries,
  type DatasetDateRange,
} from "../lib/datasetDateRange";

const DEFAULT_ANALYSIS_FILTERS: AnalysisFilters = {
  shipmentId: "",
  deviceId: "",
  position: "",
  destinationCity: "",
  destinationCountry: "",
  boxIdentifier: "",
};

type DetailTab = "consignments" | "maps" | "analysis" | "figures";

const TAB_CONFIG: Record<DetailTab, { label: string; icon: typeof Package }> = {
  consignments: { label: "Consignments", icon: Package },
  maps: { label: "Maps", icon: Map },
  analysis: { label: "Analysis", icon: BarChart3 },
  figures: { label: "Key Figures", icon: KeyRound },
};

type MetricCard = {
  key: "shipments" | "events" | "devices" | "countries";
  label: string;
  value: string;
  subValue?: string;
  icon: typeof Package;
};

export function DatasetDetailPage() {
  const { clientId, datasetId } = useParams<{ clientId: string; datasetId: string }>();
  const navigate = useNavigate();
  const perfTrace = usePagePerfTrace("DatasetDetailPage");

  const queryClient = useAppQueryClient();
  const { data: clientDatasets } = useClientDatasets(clientId, {
    perfTrace,
    perfLabel: "query.client_datasets.dataset_detail_page",
  });
  useObserveFrontQuery(perfTrace, {
    label: "query.dataset.dataset_detail_page",
    queryKey: ["dataset", clientId, datasetId],
    enabled: Boolean(clientId && datasetId),
  });
  const { data, isLoading, error } = useQuery(
    {
      queryKey: ["dataset", clientId, datasetId],
      queryFn: async () => {
        if (!clientId || !datasetId) throw new Error("Missing params");
        try {
          const { data: payload } = await fetchJsonWithPerf<{ dataset: DatasetDetail }>(
            `/api/clients/${encodeURIComponent(clientId)}/datasets/${encodeURIComponent(datasetId)}?analysis=0`,
            undefined,
            {
              label: "query.dataset.dataset_detail_page",
              queryKey: ["dataset", clientId, datasetId],
              trace: perfTrace,
            },
          );
          return payload.dataset;
        } catch (error) {
          if (error instanceof PerfHttpError) {
            throw new Error(`Dataset ${datasetId} introuvable (${error.status})`);
          }
          throw error;
        }
      },
      staleTime: 60_000,
      placeholderData: (previous) => previous,
    },
    queryClient,
  );

  const [activeTab, setActiveTab] = useState<DetailTab>("consignments");
  const dataset = data ?? null;
  const shipments = dataset?.shipments ?? [];
  const [focusedConsignments, setFocusedConsignments] = useState<string[] | null>(null);
  const [selectedDateRange, setSelectedDateRange] = useState<DatasetDateRange | null>(null);
  const [mapView, setMapView] = useState<"overview" | "reverse">("overview");
  const [isCountriesExpanded, setIsCountriesExpanded] = useState(false);
  const datasetOptions = useMemo(
    () =>
      (clientDatasets ?? []).map((item) => ({
        value: item.id,
        label: item.name,
      })),
    [clientDatasets],
  );
  const dashboardOptions = dataset?.dashboardOptions?.length
    ? dataset.dashboardOptions
    : (["operations"] as DatasetDashboardType[]);
  const dashboardType = dataset?.dashboardType ?? dashboardOptions[0];
  const hasCity = dataset?.settings?.hasCity ?? null;
  const hasBoxes = datasetHasBoxes(dataset);
  const canUseReverseMap = dataset?.settings?.hasReverseGeocoding !== false;
  const safeRange = useMemo(() => {
    const defaultMin = SAFE_TEMPERATURE_RANGE.min;
    const defaultMax = SAFE_TEMPERATURE_RANGE.max;
    const min = dataset?.settings?.temperatureMin ?? defaultMin;
    const max = dataset?.settings?.temperatureMax ?? defaultMax;
    if (!Number.isFinite(min) || !Number.isFinite(max) || min >= max) {
      return { min: defaultMin, max: defaultMax };
    }
    return { min, max };
  }, [dataset?.settings?.temperatureMax, dataset?.settings?.temperatureMin]);
  const datasetDateBounds = useMemo(() => getDatasetDateBounds(shipments), [shipments]);
  const visibleDateRange = selectedDateRange ?? datasetDateBounds;
  const filteredShipments = useMemo(
    () => filterShipmentsByDateRange(shipments, selectedDateRange),
    [selectedDateRange, shipments],
  );
  const filteredCountries = useMemo(
    () => {
      if (selectedDateRange) return getShipmentCountries(filteredShipments);
      if (dataset?.countries.length) return dataset.countries;
      return getShipmentCountries(filteredShipments);
    },
    [dataset?.countries, filteredShipments, selectedDateRange],
  );
  const filteredMap = useMemo(() => {
    if (!dataset || !selectedDateRange) return dataset?.map ?? { destinations: [], transits: [] };
    return {
      destinations: filterMapDestinations(dataset.map.destinations, filteredShipments, safeRange),
      transits: filterMapTransits(dataset.map.transits, filteredShipments),
    };
  }, [dataset, filteredShipments, safeRange, selectedDateRange]);
  const filteredFigures = useMemo(() => {
    if (!dataset || !selectedDateRange) return dataset?.figures ?? null;
    return buildFiguresForShipments(filteredShipments, filteredMap.destinations);
  }, [dataset, filteredMap.destinations, filteredShipments, selectedDateRange]);
  const configuredYAxisDomain = useMemo(() => {
    const min = dataset?.settings?.chartTemperatureMin;
    const max = dataset?.settings?.chartTemperatureMax;
    if (typeof min !== "number" || typeof max !== "number") return null;
    if (!Number.isFinite(min) || !Number.isFinite(max) || min >= max) return null;
    return [min, max] as const;
  }, [dataset?.settings?.chartTemperatureMax, dataset?.settings?.chartTemperatureMin]);
  const showAnalysisTab = dashboardType === "temperature" && Boolean(dataset?.capabilities.hasTemperature);
  const availableTabs = useMemo(
    () =>
      (showAnalysisTab
        ? (["consignments", "maps", "analysis", "figures"] as DetailTab[])
        : (["consignments", "maps", "figures"] as DetailTab[])),
    [showAnalysisTab],
  );

  useEffect(() => {
    if (!availableTabs.includes(activeTab)) {
      setActiveTab("consignments");
    }
  }, [activeTab, availableTabs]);

  useEffect(() => {
    if (mapView === "reverse" && !canUseReverseMap) {
      setMapView("overview");
    }
  }, [canUseReverseMap, mapView]);

  useEffect(() => {
    setIsCountriesExpanded(false);
    setSelectedDateRange(null);
    setFocusedConsignments(null);
  }, [datasetId]);

  useEffect(() => {
    if (isLoading || error || !dataset || !clientId || !datasetId) return;

    markFrontMilestone(perfTrace, "first_meaningful_data", {
      clientId,
      datasetId,
      shipmentCount: dataset.shipmentCount,
      eventCount: dataset.eventCount,
    });
  }, [clientId, dataset, datasetId, error, isLoading, perfTrace]);

  const metrics = useMemo(() => {
    if (!dataset) return [] as MetricCard[];
    const distinctDevices = new Set<string>();
    filteredShipments.forEach((shipment) => {
      shipment.devices.forEach((device, index) => {
        const deviceId = device.deviceId?.trim();
        if (deviceId) {
          distinctDevices.add(deviceId);
          return;
        }
        distinctDevices.add(
          [
            shipment.id,
            device.position ?? "",
            device.boxIdentifier ?? "",
            device.timeBegin ?? "",
            device.timeEnd ?? "",
            index,
          ].join("::"),
        );
      });
    });

    return [
      {
        key: "shipments",
        label: "Expéditions",
        value: filteredShipments.length.toString(),
        icon: Package,
      },
      {
        key: "events",
        label: "Événements",
        value: filteredShipments.reduce((sum, shipment) => sum + shipment.eventCount, 0).toString(),
        icon: Activity,
      },
      {
        key: "devices",
        label: "Devices",
        value: distinctDevices.size.toString(),
        icon: Cpu,
      },
      {
        key: "countries",
        label: "Pays",
        value: filteredCountries.length ? filteredCountries.length.toString() : "—",
        icon: Globe2,
      },
    ];
  }, [dataset, filteredCountries.length, filteredShipments]);

  const handleDestinationSelect = (ids: string[]) => {
    setFocusedConsignments(ids.length ? ids : null);
    setActiveTab("consignments");
    window.requestAnimationFrame(() => {
      document.querySelector(".dd-main-content")?.scrollIntoView({ behavior: "smooth" });
    });
  };

  const handleDatasetChange = (nextId: string) => {
    if (!clientId || !nextId || nextId === datasetId) return;
    setFocusedConsignments(null);
    setActiveTab("consignments");
    setMapView("overview");
    navigate(`/clients/${encodeURIComponent(clientId)}/datasets/${encodeURIComponent(nextId)}`);
  };

  const displayedShipments = useMemo(() => {
    if (!focusedConsignments || focusedConsignments.length === 0) return filteredShipments;
    const keep = new Set(focusedConsignments);
    return filteredShipments.filter((shipment) => keep.has(shipment.id));
  }, [filteredShipments, focusedConsignments]);

  const handleStartDateChange = (start: string) => {
    if (!visibleDateRange || !start) return;
    setFocusedConsignments(null);
    if (start > visibleDateRange.end) {
      setSelectedDateRange({ start, end: start });
      return;
    }
    setSelectedDateRange({ start, end: visibleDateRange.end });
  };

  const handleEndDateChange = (end: string) => {
    if (!visibleDateRange || !end) return;
    setFocusedConsignments(null);
    if (end < visibleDateRange.start) {
      setSelectedDateRange({ start: end, end });
      return;
    }
    setSelectedDateRange({ start: visibleDateRange.start, end });
  };

  const defaultCountriesLimit = 12;
  const canCollapseCountries = filteredCountries.length > defaultCountriesLimit;
  const visibleCountries =
    canCollapseCountries && !isCountriesExpanded
      ? filteredCountries.slice(0, defaultCountriesLimit)
      : filteredCountries;
  const hiddenCountriesCount = filteredCountries.length - visibleCountries.length;

  const clientPath = `/clients/${encodeURIComponent(clientId ?? "")}`;

  if (isLoading) {
    return (
      <div className="dataset-detail-page dd-state-wrap">
        <p className="dd-state-message">Chargement…</p>
      </div>
    );
  }

  if (error || !dataset) {
    return (
      <div className="dataset-detail-page dd-state-wrap">
        <p className="dd-state-message">Impossible de charger le dataset demandé.</p>
        <Link to="/clients" className="dd-back-button">
          Retour
        </Link>
      </div>
    );
  }

  return (
    <div className="dataset-detail-page dd-page-wrapper">
      <DatasetBreadcrumbNav
        clientId={clientId ?? dataset.clientId}
        clientLabel={dataset.clientId ?? clientId ?? "Client"}
        datasetId={datasetId ?? dataset.id}
        datasetLabel={dataset.name}
      />

      <div className="dd-dataset-selector-card">
        <div className="dd-selector-header">
          <div className="dd-selector-left">
            <Link to={clientPath} className="dd-back-button" aria-label="Retour à la page client">
              <span aria-hidden="true">←</span>
              <span>Retour client</span>
            </Link>
            <h2 className="dd-selector-title">Dataset {dataset.name}</h2>
            <p className="dd-selector-subtitle">
              <span>Client:</span>
              <Link to={clientPath} className="dd-selector-client-pill">
                {dataset.clientId ?? clientId}
              </Link>
            </p>
          </div>

          <div className="dd-selector-actions">
            <DatasetDateRangeControl
              bounds={datasetDateBounds}
              value={visibleDateRange}
              isFiltered={selectedDateRange !== null}
              onStartChange={handleStartDateChange}
              onEndChange={handleEndDateChange}
              onReset={() => {
                setSelectedDateRange(null);
                setFocusedConsignments(null);
              }}
            />
            {dataset.canAdmin ? (
              <Link
                className="btn btn-primary"
                to={`/clients/${encodeURIComponent(clientId ?? "")}/datasets/${encodeURIComponent(datasetId ?? "")}/data-management`}
              >
                Gestion des données
              </Link>
            ) : null}
            {datasetOptions.length ? (
              <div className="dd-selector-control">
                <label htmlFor="dataset-switch" className="dd-selector-label">
                  <CalendarDays className="dd-selector-label-icon" aria-hidden="true" />
                  <span>Changer de dataset</span>
                </label>
                <AppSelect
                  id="dataset-switch"
                  value={datasetId ?? ""}
                  options={datasetOptions}
                  onValueChange={handleDatasetChange}
                  size="lg"
                  tone="soft"
                />
              </div>
            ) : null}
          </div>
        </div>

        <div className="dd-countries-panel">
          <div className="dd-countries-header">
            <p className="dd-countries-title">Pays couverts</p>
            {canCollapseCountries ? (
              <button
                type="button"
                className="dd-countries-toggle"
                onClick={() => setIsCountriesExpanded((previous) => !previous)}
                aria-expanded={isCountriesExpanded}
                aria-controls="dataset-countries-list"
              >
                {isCountriesExpanded ? "Réduire" : `Voir tout (${filteredCountries.length})`}
              </button>
            ) : null}
          </div>
          <div className="dd-countries-list" id="dataset-countries-list">
            {visibleCountries.length ? (
              visibleCountries.map((country) => (
                <span key={country} className="dd-country-pill">
                  {country}
                </span>
              ))
            ) : (
              <span className="dd-country-pill dd-country-pill--empty">Aucun pays disponible</span>
            )}
            {!isCountriesExpanded && hiddenCountriesCount > 0 ? (
              <span className="dd-country-pill dd-country-pill--more">+{hiddenCountriesCount}</span>
            ) : null}
          </div>
        </div>
      </div>

      <main className="dd-main-content">
        <div className="dd-metrics-grid">
          {metrics.map((metric) => {
            const MetricIcon = metric.icon;
            return (
              <article key={metric.key} className={`dd-metric-card dd-metric-card--${metric.key}`}>
                <div className="dd-metric-header">
                  <div className="dd-metric-icon-wrap" aria-hidden="true">
                    <MetricIcon className="dd-metric-icon" />
                  </div>
                  <div className="dd-metric-label">{metric.label}</div>
                </div>
                <div className="dd-metric-value-row">
                  <div className={`dd-metric-value${metric.subValue ? " dd-metric-value--small" : ""}`}>
                    {metric.value}
                  </div>
                  {metric.subValue ? <span className="dd-metric-subvalue">{metric.subValue}</span> : null}
                </div>
              </article>
            );
          })}
        </div>

        <div className="dd-tabs-container">
          <div className="dd-tabs-nav">
            {availableTabs.map((tabId) => (
              <TabButton
                key={tabId}
                tabId={tabId}
                activeTab={activeTab}
                onSelect={setActiveTab}
              />
            ))}
          </div>

          <div className="dd-tab-panel">
            {activeTab === "consignments" ? (
              <>
                {focusedConsignments?.length ? (
                  <div className="dd-filter-banner">
                    <span>{focusedConsignments.length} consignment(s) sélectionné(s)</span>
                    <button type="button" onClick={() => setFocusedConsignments(null)}>
                      Réinitialiser
                    </button>
                  </div>
                ) : null}
                <ShipmentsTable
                  shipments={displayedShipments}
                  highlightedIds={focusedConsignments}
                  clientId={clientId ?? ""}
                  datasetId={datasetId ?? ""}
                  dashboardType={dashboardType}
                  hasCity={hasCity}
                  hasBoxes={hasBoxes}
                />
              </>
            ) : null}

            {activeTab === "maps" ? (
              <div className="dd-map-layout">
                <div className="dd-map-subnav">
                  <button
                    type="button"
                    className={`dd-map-toggle${mapView === "overview" ? " is-active" : ""}`}
                    onClick={() => setMapView("overview")}
                  >
                    Overview
                  </button>
                  {canUseReverseMap ? (
                    <button
                      type="button"
                      className={`dd-map-toggle${mapView === "reverse" ? " is-active" : ""}`}
                      onClick={() => setMapView("reverse")}
                    >
                      Reverse
                    </button>
                  ) : null}
                </div>
                <div className="dd-map-host">
                  {mapView !== "reverse" ? (
                    <MapPanel
                      destinations={filteredMap.destinations}
                      transits={filteredMap.transits}
                      uiScale={0.75}
                      onSelectDestination={handleDestinationSelect}
                    />
                  ) : (
                    <MapReversePanel destinations={filteredMap.destinations} uiScale={0.75} />
                  )}
                </div>
              </div>
            ) : null}

            {activeTab === "analysis" && showAnalysisTab ? (
              <AnalysisPanel
                clientId={clientId ?? ""}
                datasetId={datasetId ?? ""}
                initialFilters={{ ...DEFAULT_ANALYSIS_FILTERS }}
                perfTrace={perfTrace}
                safeRange={safeRange}
                yAxisDomain={configuredYAxisDomain}
                hasCity={hasCity}
                hasBoxes={hasBoxes}
                dateRange={selectedDateRange}
              />
            ) : null}

            {activeTab === "figures" && filteredFigures ? (
              <KeyFiguresPanel
                figures={filteredFigures}
                dashboardType={dashboardType}
                shipments={filteredShipments}
                safeRange={safeRange}
                yAxisDomain={configuredYAxisDomain}
                hasBoxes={hasBoxes}
              />
            ) : null}
          </div>
        </div>
      </main>
    </div>
  );
}

type DatasetDateRangeControlProps = {
  bounds: DatasetDateRange | null;
  value: DatasetDateRange | null;
  isFiltered: boolean;
  onStartChange: (value: string) => void;
  onEndChange: (value: string) => void;
  onReset: () => void;
};

export function DatasetDateRangeControl({
  bounds,
  value,
  isFiltered,
  onStartChange,
  onEndChange,
  onReset,
}: DatasetDateRangeControlProps) {
  return (
    <div className="dd-date-range-control">
      <div className="dd-date-range-heading">
        <span className="dd-selector-label">
          <CalendarDays className="dd-selector-label-icon" aria-hidden="true" />
          Période
        </span>
      </div>
      <div className="dd-date-range-inputs">
        <DatePicker
          label="Du"
          value={value?.start ?? ""}
          min={bounds?.start}
          max={value?.end ?? bounds?.end}
          rangeStart={value?.start}
          rangeEnd={value?.end}
          disabled={!bounds}
          onChange={onStartChange}
        />
        <span className="dd-date-range-arrow" aria-hidden="true">→</span>
        <DatePicker
          label="Au"
          value={value?.end ?? ""}
          min={value?.start ?? bounds?.start}
          max={bounds?.end}
          rangeStart={value?.start}
          rangeEnd={value?.end}
          disabled={!bounds}
          onChange={onEndChange}
        />
        {isFiltered ? (
          <button type="button" className="dd-date-range-clear" onClick={onReset}>
            <X aria-hidden="true" />
            Effacer
          </button>
        ) : null}
      </div>
    </div>
  );
}

type TabButtonProps = {
  tabId: DetailTab;
  activeTab: DetailTab;
  onSelect: (tab: DetailTab) => void;
};

function TabButton({ tabId, activeTab, onSelect }: TabButtonProps) {
  const isActive = activeTab === tabId;
  const { label, icon: Icon } = TAB_CONFIG[tabId];
  return (
    <button
      type="button"
      className={`dd-tab-button${isActive ? " is-active" : ""}`}
      onClick={() => onSelect(tabId)}
    >
      <Icon className="dd-tab-icon" aria-hidden="true" />
      {label}
    </button>
  );
}

function datasetHasBoxes(dataset: DatasetDetail | null): boolean {
  if (!dataset) return false;
  if (dataset.settings.hasBoxes === false) return false;
  if (dataset.settings.hasBoxes === true) return true;

  return dataset.shipments.some((shipment) => {
    if (shipment.boxIdentifier) return true;
    return shipment.devices.some((device) => Boolean(device.boxIdentifier));
  });
}
